import { pointAlongSegment, type BoundaryPoint } from "./geo";
import type {
  GridEdgeRef,
  GridMesh,
  GridVertex,
  VertexDelta,
} from "./gridMeshTypes";

const clampUnit = (value: number): number => Math.min(1, Math.max(0, value));

const parameterAlongEdge = (
  point: BoundaryPoint,
  start: BoundaryPoint,
  end: BoundaryPoint,
): number => {
  const deltaLongitude = end.longitude - start.longitude;
  const deltaLatitude = end.latitude - start.latitude;
  const lengthSq =
    deltaLongitude * deltaLongitude + deltaLatitude * deltaLatitude;
  if (lengthSq === 0) {
    return 0;
  }

  return clampUnit(
    ((point.longitude - start.longitude) * deltaLongitude +
      (point.latitude - start.latitude) * deltaLatitude) /
      lengthSq,
  );
};

/** Place row-divider vertices back on their plot edges from the stored fraction. */
const snapEdgeConstrainedVertices = (
  vertices: Record<string, GridVertex>,
): Record<string, GridVertex> => {
  const next: Record<string, GridVertex> = { ...vertices };
  Object.values(vertices).forEach((vertex) => {
    const edge = vertex.onEdge;
    if (edge == null) {
      return;
    }
    const start = next[edge.startVertexId];
    const end = next[edge.endVertexId];
    if (start == null || end == null) {
      return;
    }
    const point = pointAlongSegment(start, end, edge.t);
    next[vertex.id] = {
      ...vertex,
      latitude: point.latitude,
      longitude: point.longitude,
    };
  });
  return next;
};

/** Move one vertex; all cells that use its id follow automatically. */
export const moveVertex = (
  mesh: GridMesh,
  vertexId: string,
  point: BoundaryPoint,
): GridMesh => {
  const existing = mesh.vertices[vertexId];
  if (existing == null) {
    return mesh;
  }

  const withVertices = (vertices: Record<string, GridVertex>): GridMesh => ({
    cells: mesh.cells,
    ...(mesh.rows == null ? {} : { rows: mesh.rows }),
    vertices,
  });

  // Row divider vertex: slide along its plot edge. No edges have moved, so other
  // vertices stay where they are.
  if (existing.onEdge != null) {
    const start = mesh.vertices[existing.onEdge.startVertexId];
    const end = mesh.vertices[existing.onEdge.endVertexId];
    let placed = point;
    let t = existing.onEdge.t;
    if (start != null && end != null) {
      t = parameterAlongEdge(point, start, end);
      placed = pointAlongSegment(start, end, t);
    }

    return withVertices({
      ...mesh.vertices,
      [vertexId]: {
        ...existing,
        latitude: placed.latitude,
        longitude: placed.longitude,
        onEdge: { ...existing.onEdge, t },
      },
    });
  }

  // Plot corner: move freely, then place row dividers back on the updated edges.
  return withVertices(
    snapEdgeConstrainedVertices({
      ...mesh.vertices,
      [vertexId]: {
        ...existing,
        latitude: point.latitude,
        longitude: point.longitude,
      },
    }),
  );
};

/** Translate both endpoints of an edge by the same delta. */
export const moveEdge = (
  mesh: GridMesh,
  edge: GridEdgeRef,
  delta: VertexDelta,
): GridMesh => {
  let next = moveVertex(mesh, edge.startVertexId, {
    latitude:
      (mesh.vertices[edge.startVertexId]?.latitude ?? 0) + delta.latitude,
    longitude:
      (mesh.vertices[edge.startVertexId]?.longitude ?? 0) + delta.longitude,
  });
  next = moveVertex(next, edge.endVertexId, {
    latitude: (next.vertices[edge.endVertexId]?.latitude ?? 0) + delta.latitude,
    longitude:
      (next.vertices[edge.endVertexId]?.longitude ?? 0) + delta.longitude,
  });
  return next;
};

/** Translate every vertex in a chain (deduped) by the same delta. */
export const moveEdgeChain = (
  mesh: GridMesh,
  vertexIds: string[],
  delta: VertexDelta,
): GridMesh => {
  let next = mesh;
  const unique = [...new Set(vertexIds)];
  unique.forEach((vertexId) => {
    const vertex = next.vertices[vertexId];
    if (vertex == null) {
      return;
    }
    next = moveVertex(next, vertexId, {
      latitude: vertex.latitude + delta.latitude,
      longitude: vertex.longitude + delta.longitude,
    });
  });
  return next;
};

/** Plot-ring vertices of `cellIds`, excluding row dividers. */
export const cellRingVertexOrigins = (
  mesh: GridMesh,
  cellIds: readonly string[],
): Record<string, BoundaryPoint> => {
  const selected = new Set(cellIds);
  const origins: Record<string, BoundaryPoint> = {};
  mesh.cells.forEach((cell) => {
    if (!selected.has(cell.id)) {
      return;
    }
    cell.vertexIds.forEach((vertexId) => {
      const vertex = mesh.vertices[vertexId];
      if (vertex == null || vertex.onEdge != null) {
        return;
      }
      origins[vertexId] = {
        latitude: vertex.latitude,
        longitude: vertex.longitude,
      };
    });
  });
  return origins;
};

/** Centroid of plot-ring vertices. Null when the group has no vertices. */
export const cellGroupCenter = (
  origins: Record<string, BoundaryPoint>,
): BoundaryPoint | null => {
  const points = Object.values(origins);
  if (points.length === 0) {
    return null;
  }

  const latitude =
    points.reduce((sum, point) => sum + point.latitude, 0) / points.length;
  const longitude =
    points.reduce((sum, point) => sum + point.longitude, 0) / points.length;
  return { latitude, longitude };
};

const rotateAround = (
  origin: BoundaryPoint,
  center: BoundaryPoint,
  degrees: number,
): BoundaryPoint => {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const x = origin.longitude - center.longitude;
  const y = origin.latitude - center.latitude;
  return {
    latitude: center.latitude + x * sin + y * cos,
    longitude: center.longitude + x * cos - y * sin,
  };
};

/**
 * Rotate `origins` around `center` by `degrees` (CCW positive). Vertices not
 * listed stay put. Row dividers are placed back onto their plot edges.
 */
export const rotateCellsFromOrigins = (
  mesh: GridMesh,
  origins: Record<string, BoundaryPoint>,
  center: BoundaryPoint,
  degrees: number,
): GridMesh => {
  const vertices: Record<string, GridVertex> = { ...mesh.vertices };
  Object.entries(origins).forEach(([vertexId, origin]) => {
    const existing = vertices[vertexId];
    if (existing == null || existing.onEdge != null) {
      return;
    }
    const rotated = rotateAround(origin, center, degrees);
    vertices[vertexId] = {
      ...existing,
      latitude: rotated.latitude,
      longitude: rotated.longitude,
    };
  });

  return {
    cells: mesh.cells,
    ...(mesh.rows == null ? {} : { rows: mesh.rows }),
    vertices: snapEdgeConstrainedVertices(vertices),
  };
};

/**
 * Translate all vertices belonging to the selected cells.
 * Callers that need an independent island should detach shared vertices first.
 */
export const translateCells = (
  mesh: GridMesh,
  selectedIds: string[],
  delta: VertexDelta,
): GridMesh => {
  const selected = new Set(selectedIds);

  // Collect unique vertex ids from the selection (shared corners counted once).
  const movingIds = new Set<string>();
  mesh.cells.forEach((cell) => {
    if (!selected.has(cell.id)) {
      return;
    }
    cell.vertexIds.forEach((vertexId) => movingIds.add(vertexId));
  });

  const vertices: Record<string, GridVertex> = { ...mesh.vertices };
  movingIds.forEach((vertexId) => {
    const vertex = vertices[vertexId];
    if (vertex == null) {
      return;
    }
    vertices[vertexId] = {
      ...vertex,
      latitude: vertex.latitude + delta.latitude,
      longitude: vertex.longitude + delta.longitude,
    };
  });

  return {
    cells: mesh.cells,
    ...(mesh.rows == null ? {} : { rows: mesh.rows }),
    vertices: snapEdgeConstrainedVertices(vertices),
  };
};
